import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { translations, LANGUAGES, DEFAULT_LANGUAGE } from './translations';
import { appResources } from './app-namespace';
import { parityResources } from './parity';

export const STORAGE_KEY = 'aura_lang';
export const SUPPORTED_LANGUAGES = LANGUAGES.map((l) => l.code) as string[];
export { LANGUAGES, DEFAULT_LANGUAGE };

/**
 * Ключи лендинга исторически плоские с точками ('nav.home'), а keySeparator
 * в i18next по умолчанию — '.'. Разворачиваем их во вложенные объекты, чтобы
 * не переписывать словари и не терять привычные вызовы t('nav.home').
 */
function unflatten(flat: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split('.');
    let node = out;
    for (let i = 0; i < parts.length - 1; i += 1) {
      const part = parts[i];
      if (typeof node[part] !== 'object' || node[part] === null) node[part] = {};
      node = node[part] as Record<string, unknown>;
    }
    node[parts[parts.length - 1]] = value;
  }
  return out;
}

const resources = Object.fromEntries(
  SUPPORTED_LANGUAGES.map((lng) => [
    lng,
    {
      landing: unflatten(
        (translations as Record<string, Record<string, string>>)[lng] ?? {}
      ),
      app: appResources[lng] ?? {},
      parity: parityResources[lng] ?? {},
    },
  ])
);

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    supportedLngs: SUPPORTED_LANGUAGES,
    fallbackLng: DEFAULT_LANGUAGE,
    // 'landing' первым — старые вызовы t('nav.home') без namespace работают как раньше.
    ns: ['landing', 'app', 'parity'],
    defaultNS: 'landing',
    detection: {
      // Тот же ключ, что и у прежнего LanguageContext — выбор языка не сбрасывается.
      lookupLocalStorage: STORAGE_KEY,
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
    returnNull: false,
  });

/** <html lang="..."> держим в синхроне для доступности и SEO. */
function syncHtmlLang(lng: string) {
  if (typeof document !== 'undefined') document.documentElement.lang = lng;
}

syncHtmlLang(i18n.language);
i18n.on('languageChanged', syncHtmlLang);

export default i18n;
